import { Box, Stack, Text, Group, ThemeIcon } from '@mantine/core'
import {
  IconBug,
  IconAlertOctagon,
  IconTrendingDown,
  IconTrendingUp,
  IconCircleCheck,
  IconAlertTriangle,
  IconChartBar,
} from '@tabler/icons-react'
import { WidgetCard } from '@/components/ui/WidgetCard'
import { CardHeader } from '@/components/ui/CardHeader'
import { useJiraSummary, useJiraVelocity } from '@/hooks/useJiraData'
import type { JiraDashboardSummary, JiraVelocityData } from '@/api/jira'

// === TYPES ===

interface Insight {
  icon: React.ReactNode
  text: string
  severity: 'critical' | 'warning' | 'info' | 'success'
}

// === CONSTANTS ===

const SEVERITY_COLORS: Record<Insight['severity'], string> = {
  critical: '#ff5b57',
  warning:  '#f59c1a',
  info:     '#098d93',
  success:  '#2f9e44',
}

const SEVERITY_THEME_COLORS: Record<Insight['severity'], string> = {
  critical: 'red',
  warning:  'yellow',
  info:     'teal',
  success:  'green',
}

// === INSIGHT GENERATION ===

function generateInsights(
  summary: JiraDashboardSummary,
  velocity?: JiraVelocityData,
): Insight[] {
  const insights: Insight[] = []
  const total = summary.total_issues || 1

  // Critical: bugs
  if (summary.bugs_open > 3) {
    insights.push({
      icon: <IconBug size={12} />,
      text: `${summary.bugs_open} bugs abertos — acao necessaria`,
      severity: 'critical',
    })
  } else if (summary.bugs_open > 0) {
    insights.push({
      icon: <IconBug size={12} />,
      text: `${summary.bugs_open} bug(s) aberto(s)`,
      severity: 'warning',
    })
  }

  // Critical: impediments
  if (summary.impediments > 0) {
    insights.push({
      icon: <IconAlertOctagon size={12} />,
      text: `${summary.impediments} impedimento(s) bloqueando progresso`,
      severity: 'critical',
    })
  }

  // Velocity trend
  if (velocity?.trend === 'declining') {
    insights.push({
      icon: <IconTrendingDown size={12} />,
      text: 'Velocity em declinio nos últimos sprints',
      severity: 'warning',
    })
  } else if (velocity?.trend === 'improving') {
    insights.push({
      icon: <IconTrendingUp size={12} />,
      text: 'Velocity melhorando — bom ritmo!',
      severity: 'success',
    })
  }

  // WIP ratio
  const wipRatio = (summary.by_status_category?.in_progress || 0) / total
  if (wipRatio > 0.5) {
    insights.push({
      icon: <IconChartBar size={12} />,
      text: `${Math.round(wipRatio * 100)}% do backlog em andamento — risco de dispersao`,
      severity: 'warning',
    })
  }

  // Done ratio
  const doneRatio = (summary.by_status_category?.done || 0) / total
  if (doneRatio > 0.3) {
    insights.push({
      icon: <IconCircleCheck size={12} />,
      text: `${Math.round(doneRatio * 100)}% das issues concluidas`,
      severity: 'success',
    })
  } else if (doneRatio < 0.1) {
    insights.push({
      icon: <IconAlertTriangle size={12} />,
      text: `Apenas ${Math.round(doneRatio * 100)}% das issues concluídas`,
      severity: 'warning',
    })
  }

  // No bugs, no impediments = good
  if (summary.bugs_open === 0 && summary.impediments === 0) {
    insights.push({
      icon: <IconCircleCheck size={12} />,
      text: 'Nenhum bug ou impedimento ativo — projeto saudável',
      severity: 'success',
    })
  }

  return insights
}

// === INSIGHT ROW ===

function InsightRow({ insight }: { insight: Insight }) {
  const color = SEVERITY_COLORS[insight.severity]
  const themeColor = SEVERITY_THEME_COLORS[insight.severity]

  return (
    <Box
      style={{
        borderLeft: `3px solid ${color}`,
        paddingLeft: 10,
        paddingTop: 5,
        paddingBottom: 5,
        borderRadius: '0 4px 4px 0',
        background: `${color}0a`,
      }}
    >
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <ThemeIcon
          size={20}
          radius="sm"
          variant="light"
          color={themeColor}
          style={{ flexShrink: 0, marginTop: 1 }}
        >
          {insight.icon}
        </ThemeIcon>
        <Text size="xs" style={{ lineHeight: 1.5 }}>
          {insight.text}
        </Text>
      </Group>
    </Box>
  )
}

// === MAIN COMPONENT ===

export default function JiraInsights() {
  const { data: summary, isLoading } = useJiraSummary()
  const { data: velocity } = useJiraVelocity()

  if (isLoading || !summary) return null

  const insights = generateInsights(summary, velocity).slice(0, 6)

  if (insights.length < 2) return null

  return (
    <WidgetCard accent="warning" p={0}>
      <CardHeader title="⚡ Insights" />
      <Box p="md">
        <Stack gap="xs">
          {insights.map((insight, i) => (
            <InsightRow key={i} insight={insight} />
          ))}
        </Stack>
      </Box>
    </WidgetCard>
  )
}
